/**
 * RETOBLOCK BLE TRANSPORT LAYER
 * Quản lý kết nối truyền thông: Web Bluetooth API thực tế + Mock BLE Simulator tích hợp.
 */

import {
  BLE_CONFIG,
  COMMANDS,
  buildHeartbeatPacket,
  buildPingPacket,
  buildStopPacket,
  buildFileStartPacket,
  buildFileDataPacket,
  buildFileEndPacket,
  buildGetLogsPacket,
  preparePythonChunks,
  calculateCRC32,
  buildOtaBeginFrame,
  buildOtaDataFrame,
  buildOtaEndFrame,
  buildOtaAbortFrame,
  parseOtaReady,
  parseOtaResult,
  parseOtaAbort,
  parseOtaAck,
  parseLogDataPacket,
  parseFrame,
  parseTelemetry,
  parseRuntimeError
} from './ble-protocol.js';

import { MockRobotServer } from './mock-robot.js';

export class BleTransportManager {
  constructor() {
    this.isMockMode = false; // Mặc định kết nối phần cứng thật cho người dùng cuối (End User)
    this.mockRobot = new MockRobotServer();

    // Web Bluetooth Real Device references
    this.bluetoothDevice = null;
    this.gattServer = null;
    this.rxCharacteristic = null;
    this.txCharacteristic = null;

    // Connection state
    this.isConnected = false;
    this.isConnecting = false;
    this.deviceName = '';
    this.connectedDevice = null;

    // Keep-alive heartbeat: Web Worker dedicated thread (Anti-throttling) + fallback
    this.heartbeatWorker = null;
    this.heartbeatWorkerUrl = null;
    this.heartbeatInterval = null;

    // Outbound GATT transmission queue (Write Mutex / Serialization)
    this.txQueue = [];
    this.isSending = false;

    // Upload state tracking
    this.isUploading = false;
    this.scriptLineOffset = 0;

    // BLE Fast OTA state
    this.isOtaActive = false;
    this.otaEndFrameSent = false;
    this.otaBytesSent = 0;
    this.otaTotalBytes = 0;
    this.otaLastAckChunk = 0;
    this.otaPendingResolvers = {};
    this.onOtaProgress = null;
    this.onOtaAck = null;
    this.onOtaResult = null;
    this.firmwareVersion = null;

    // Log Stream state
    this.logStreamResolver = null;
    this.onLogChunk = null;

    // Event callbacks
    this.onStatusChange = null;
    this.onTelemetry = null;
    this.onRuntimeError = null;
    this.onLogMessage = null;

    // Setup Mock robot notification pipe
    this.mockRobot.setNotifyCallback((packetBytes) => {
      this.handleIncomingBytes(packetBytes);
    });

    // Lắng nghe sự kiện chuyển tab (Visibility Change) để bắn heartbeat ngay lập tức khi tab quay lại trạng thái nhìn thấy
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isConnected && !this.isOtaActive) {
          this.sendHeartbeat();
        }
      });
    }
  }

  log(msg, type = 'info') {
    if (this.onLogMessage) {
      this.onLogMessage(msg, type);
    } else {
      console.log(`[BLE LOG] [${type.toUpperCase()}] ${msg}`);
    }
  }

  setMockMode(enable) {
    if (this.isConnected) {
      this.disconnect();
    }
    this.isMockMode = enable;
    this.log(`Chuyển sang chế độ: ${enable ? 'Giả lập Robot ảo (Mock Simulator)' : 'Bluetooth phần cứng (Web Bluetooth)'}`);
  }

  async scanDevices() {
    if (this.isMockMode) {
      // Danh sách các xe giả lập trong phòng học theo tài liệu
      return [
        { id: 'mock-1', name: 'RETOCAR_STARTER_A12', rssi: -55, isMock: true },
        { id: 'mock-2', name: 'RETOCAR_STARTER_B04', rssi: -78, isMock: true },
        { id: 'mock-3', name: 'RETOCAR_STARTER_C99', rssi: -84, isMock: true }
      ];
    }

    // Web Bluetooth API scan
    if (!navigator.bluetooth) {
      throw new Error('Trình duyệt này không hỗ trợ Web Bluetooth API. Hãy sử dụng Google Chrome hoặc Edge, hoặc bật chế độ Giả lập Robot.');
    }

    try {
      this.log('Đang mở hộp thoại quét thiết bị Bluetooth gần bạn...');
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'RETOCAR' }],
        optionalServices: [BLE_CONFIG.SERVICE_UUID]
      });

      return [{
        id: device.id,
        name: device.name || 'RETOCAR_UNKNOWN',
        rssi: -60,
        rawDevice: device,
        isMock: false
      }];
    } catch (err) {
      if (err.name === 'NotFoundError') {
        this.log('Người dùng đã hủy tìm kiếm thiết bị BLE.', 'warning');
        return [];
      }
      throw err;
    }
  }

  async connect(deviceInfo) {
    this.isConnecting = true;
    this.updateStatus('connecting');

    try {
      if (deviceInfo.isMock || this.isMockMode) {
        this.isMockMode = !!deviceInfo.isMock;
        this.log(`Đang kết nối tới ${deviceInfo.name}...`);
        await new Promise(r => setTimeout(r, 600)); // Delay nhỏ mô phỏng bắt tay

        this.mockRobot.name = deviceInfo.name;
        this.mockRobot.connect();
        this.isConnected = true;
        this.deviceName = deviceInfo.name;
        this.connectedDevice = deviceInfo;
      } else {
        // Thực hiện kết nối GATT Web Bluetooth thật
        const device = deviceInfo.rawDevice;
        this.bluetoothDevice = device;
        device.addEventListener('gattserverdisconnected', () => this.handleDisconnection());

        this.log(`Đang bắt tay GATT với ${device.name}...`);
        this.gattServer = await device.gatt.connect();

        const service = await this.gattServer.getPrimaryService(BLE_CONFIG.SERVICE_UUID);
        this.rxCharacteristic = await service.getCharacteristic(BLE_CONFIG.RX_UUID);
        this.txCharacteristic = await service.getCharacteristic(BLE_CONFIG.TX_UUID);

        // Đăng ký nhận Notify gói Telemetry
        await this.txCharacteristic.startNotifications();
        this.txCharacteristic.addEventListener('characteristicvaluechanged', (e) => {
          const value = new Uint8Array(e.target.value.buffer);
          this.handleIncomingBytes(value);
        });

        this.isConnected = true;
        this.deviceName = device.name;
        this.connectedDevice = deviceInfo;
      }

      this.isConnecting = false;
      this.updateStatus('connected');
      this.log(`Kết nối BLE thành công với ${this.deviceName}!`, 'success');
      this.startHeartbeat();
      return true;
    } catch (err) {
      this.isConnecting = false;
      this.updateStatus('disconnected');
      this.log(`Lỗi kết nối BLE: ${err.message}`, 'error');
      throw err;
    }
  }

  disconnect() {
    this.abortUpload();
    this.abortOta();
    this.stopHeartbeat();
    this.clearTxQueue();
    if (this.isMockMode && this.mockRobot) {
      this.mockRobot.disconnect();
    }
    if (this.gattServer && this.gattServer.connected) {
      this.gattServer.disconnect();
    }

    this.handleDisconnection();
  }

  handleDisconnection() {
    // Graceful Disconnection Detection (Fail-Safe OTA Fallback):
    // Trong quá trình thực hiện OTA, nếu nhận sự kiện ngắt kết nối (gattserverdisconnected):
    // Nếu điều kiện: bytes_sent >= total_bytes (đã truyền đủ 100% gói tin) VÀ cmd_end_sent === true:
    // Khi xe ngắt kết nối BLE, tự động kích hoạt trạng thái THÀNH CÔNG (vì xe đã ghi Flash xong và đang reboot),
    // hủy bỏ bộ đếm Timeout 25s ngay lập tức mà không cần chờ gói OTA_RESULT.
    if (this.isOtaActive && this.otaEndFrameSent && this.otaBytesSent >= this.otaTotalBytes && this.otaTotalBytes > 0) {
      this.log('[OTA] ESP32 đã hoàn tất nạp Flash và ngắt kết nối để khởi động lại (Graceful Disconnect Fallback).', 'success');
      if (this.otaPendingResolvers.result) {
        const resolver = this.otaPendingResolvers.result;
        clearTimeout(resolver.timer);
        delete this.otaPendingResolvers.result;
        resolver.resolve({
          isSuccess: true,
          resultCode: 0x00,
          message: 'Nâng cấp Firmware thành công! Robot đang tự động reboot (khởi động lại) vào bản v1.1.12...',
          rebootCountdown: 2
        });
      }
    }

    this.stopHeartbeat();
    this.clearTxQueue();
    const wasConnected = this.isConnected;
    this.isConnected = false;
    this.isConnecting = false;
    this.connectedDevice = null;
    this.firmwareVersion = null;
    this.updateStatus('disconnected');

    if (wasConnected) {
      this.log(`Đã ngắt kết nối BLE với ${this.deviceName}`, 'warning');
    }
  }

  updateStatus(status) {
    if (this.onStatusChange) {
      this.onStatusChange({
        status,
        isConnected: this.isConnected,
        deviceName: this.deviceName
      });
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();

    // 1. Sử dụng Web Worker luồng riêng biệt để chống hiện tượng Browser Throttling khi tab chạy nền (Chromium giãn timer >= 1000ms)
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      try {
        const workerCode = `
          let timer = null;
          self.onmessage = function(e) {
            if (e.data === 'start') {
              if (timer) clearInterval(timer);
              timer = setInterval(() => {
                self.postMessage('tick');
              }, 200);
            } else if (e.data === 'stop') {
              if (timer) {
                clearInterval(timer);
                timer = null;
              }
            }
          };
        `;
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        this.heartbeatWorkerUrl = URL.createObjectURL(blob);
        this.heartbeatWorker = new Worker(this.heartbeatWorkerUrl);

        this.heartbeatWorker.onmessage = (e) => {
          if (e.data === 'tick' && this.isConnected) {
            this.sendHeartbeat();
          }
        };

        this.heartbeatWorker.postMessage('start');
        return;
      } catch (err) {
        console.warn('Không thể khởi tạo Heartbeat Web Worker, chuyển sang Fallback Timer:', err);
      }
    }

    // 2. Fallback sang setInterval cho môi trường Node.js (unit test) hoặc trình duyệt không hỗ trợ Web Worker
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this.sendHeartbeat();
      }
    }, 200);
  }

  stopHeartbeat() {
    if (this.heartbeatWorker) {
      try {
        this.heartbeatWorker.postMessage('stop');
        this.heartbeatWorker.terminate();
      } catch (e) {}
      this.heartbeatWorker = null;
    }
    if (this.heartbeatWorkerUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      try {
        URL.revokeObjectURL(this.heartbeatWorkerUrl);
      } catch (e) {}
      this.heartbeatWorkerUrl = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Truyền gói tin nhị phân qua BLE GATT với cơ chế hàng đợi khóa tuần tự (Write Mutex)
   * Tránh xung đột writeValueWithoutResponse khi nhiều tiến trình gọi cùng lúc.
   * @param {Uint8Array} frameBytes - Dữ liệu gói tin nhị phân
   * @param {boolean} isPriority - Nếu true (lệnh khẩn cấp), đưa lên đầu hàng đợi
   */
  async sendRawPacket(frameBytes, isPriority = false) {
    if (!this.isConnected) return false;

    if (this.isMockMode || (this.connectedDevice && this.connectedDevice.isMock)) {
      this.mockRobot.handleIncomingData(frameBytes);
      return true;
    }

    if (!this.rxCharacteristic) return false;

    return new Promise((resolve) => {
      const task = { frameBytes, resolve };
      if (isPriority) {
        this.txQueue.unshift(task);
      } else {
        this.txQueue.push(task);
      }
      this._processTxQueue();
    });
  }

  async _processTxQueue() {
    if (this.isSending || this.txQueue.length === 0) return;

    this.isSending = true;
    const task = this.txQueue.shift();

    try {
      if (this.rxCharacteristic && this.isConnected) {
        // Bảo vệ chống treo writeValueWithoutResponse khi Windows Bluetooth stack bị nghẽn
        const writePromise = this.rxCharacteristic.writeValueWithoutResponse(task.frameBytes);
        let timer = null;
        const timeoutPromise = new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('GATT write timeout (1000ms)')), 1000);
        });
        try {
          await Promise.race([writePromise, timeoutPromise]);
          task.resolve(true);
        } finally {
          if (timer) clearTimeout(timer);
        }
      } else {
        task.resolve(false);
      }
    } catch (err) {
      console.error('Lỗi gửi GATT write:', err);
      task.resolve(false);
    } finally {
      this.isSending = false;
      if (this.txQueue.length > 0) {
        this._processTxQueue();
      }
    }
  }

  /**
   * Xóa sạch các gói tin còn tồn trong hàng đợi truyền
   */
  clearTxQueue() {
    while (this.txQueue.length > 0) {
      const task = this.txQueue.shift();
      task.resolve(false);
    }
    this.isSending = false;
  }

  sendHeartbeat() {
    // Nếu trong hàng đợi txQueue đã có sẵn một gói Heartbeat đang chờ truyền,
    // bỏ qua gói mới để tránh làm nghẽn hàng đợi GATT và gây delay tích lũy trên Bluetooth stack.
    const isHeartbeatPending = this.txQueue.some(
      (task) => task.frameBytes && task.frameBytes.length >= 3 && task.frameBytes[2] === COMMANDS.HEARTBEAT
    );
    if (isHeartbeatPending) {
      return Promise.resolve(true);
    }
    const packet = buildHeartbeatPacket();
    return this.sendRawPacket(packet, true);
  }

  async sendPing(targetDevice = null) {
    this.log('Gửi tín hiệu Ping nhận diện xe (nháy đèn & bíp)...', 'info');
    const packet = buildPingPacket();

    if (targetDevice && targetDevice.isMock) {
      this.mockRobot.handlePing();
      return true;
    }

    return this.sendRawPacket(packet);
  }

  abortUpload() {
    if (this.isUploading) {
      this.isUploading = false;
      this.log('Đã dừng tiến trình nạp mã nguồn do ngắt/lỗi.', 'warning');
    }
  }

  async sendEmergencyStop() {
    this.abortUpload();
    this.log('[KHẨN CẤP] Gửi lệnh STOP khẩn cấp (0xFF) xuống vi điều khiển!', 'error');
    const packet = buildStopPacket();
    // Ưu tiên cao nhất: isPriority = true để lệnh STOP được truyền ngay lập tức
    const sent = await this.sendRawPacket(packet, true);
    if (this.isMockMode) {
      this.mockRobot.emergencyStop();
    }
    return sent;
  }


  /**
   * Truyền toàn bộ file mã nguồn Python theo cơ chế Chunking:
   * 0x20 (File Start) -> 0x21 (Chunks <= 120B) -> 0x22 (File End với CRC-16)
   */
  async sendPythonScript(pythonCode, onProgress = null) {
    if (!this.isConnected) {
      throw new Error('Chưa kết nối với Robot! Vui lòng kết nối BLE trước khi nạp code.');
    }

    this.isUploading = true;
    try {
      let scriptToUpload = pythonCode;
      this.scriptLineOffset = 0;

      // Tương thích ngược: Đảm bảo reset_timer() và time_now() hoạt động ổn định trên cả robot
      // chạy Firmware xuất xưởng v1.0.0 (chưa flash firmware C mới) lẫn firmware v1.1.0+
      if (pythonCode.includes('reset_timer') || pythonCode.includes('time_now')) {
        const shim = `try:
    RetoCar.reset_timer
except AttributeError:
    _NativeRetoCar = RetoCar
    class RetoCar:
        def __init__(self):
            self._dev = _NativeRetoCar()
            self._start_timer = time.ticks_ms()
        def reset_timer(self):
            if hasattr(self._dev, 'reset_timer'):
                try: self._dev.reset_timer()
                except Exception: pass
            self._start_timer = time.ticks_ms()
        def time_now(self):
            if hasattr(self._dev, 'time_now'):
                try: return self._dev.time_now()
                except Exception: pass
            return (time.ticks_ms() - getattr(self, '_start_timer', 0)) / 1000
        def __getattr__(self, name):
            return getattr(self._dev, name)
`;
        if (pythonCode.includes('import time\n')) {
          scriptToUpload = pythonCode.replace('import time\n', `import time\n\n${shim}\n`);
          const shimLines = (shim.match(/\n/g) || []).length + 2;
          this.scriptLineOffset = shimLines;
        }
      }

      const { fileSize, totalChunks, chunks, crc16 } = preparePythonChunks(scriptToUpload);
      this.log(`Bắt đầu nạp mã nguồn: ${fileSize} bytes, chia thành ${totalChunks} chunks (CRC: 0x${crc16.toString(16)})...`, 'info');

      if (onProgress) onProgress(5);

      if (!this.isUploading || !this.isConnected) {
        throw new Error('Tiến trình nạp code đã bị hủy!');
      }

      // 1. Gói 0x20: File Start
      const startPacket = buildFileStartPacket(fileSize, totalChunks);
      await this.sendRawPacket(startPacket);
      await new Promise(r => setTimeout(r, 25));

      // 2. Các gói 0x21: File Data Chunks
      for (let i = 0; i < totalChunks; i++) {
        if (!this.isUploading || !this.isConnected) {
          throw new Error('Tiến trình nạp code đã bị hủy!');
        }

        const chunkPacket = buildFileDataPacket(i, chunks[i]);
        await this.sendRawPacket(chunkPacket);

        const progress = Math.round(5 + ((i + 1) / totalChunks) * 85);
        if (onProgress && this.isUploading) onProgress(progress);

        // Nhịp truyền chống tràn bộ đệm BLE
        await new Promise(r => setTimeout(r, 15));
      }

      if (!this.isUploading || !this.isConnected) {
        throw new Error('Tiến trình nạp code đã bị hủy!');
      }

      // 3. Gói 0x22: File End & Trigger Run
      const endPacket = buildFileEndPacket(crc16);
      await this.sendRawPacket(endPacket);

      if (onProgress && this.isUploading) onProgress(100);
      this.log('Nạp mã nguồn hoàn tất! Đang kích hoạt Core 1 thực thi Live Mode...', 'success');
      return true;
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * ==============================================================================
   * BLE FAST OTA (DIRECT BLUETOOTH STREAMING)
   * 0x30 (Begin) -> 0x31 (Ready) -> 0x32 (Data stream) -> 0x33 (End) -> 0x34 (Result)
   * ==============================================================================
   */

  async uploadFirmwareOta(binaryBuffer, progressCallback = null) {
    if (!this.isConnected) {
      throw new Error('Chưa kết nối với Robot! Vui lòng kết nối BLE trước khi cập nhật firmware.');
    }

    if (this.isUploading || this.isOtaActive) {
      throw new Error('Hệ thống đang bận truyền dữ liệu khác!');
    }

    this.isOtaActive = true;
    this.otaEndFrameSent = false;
    this.otaBytesSent = 0;
    this.otaLastAckChunk = 0;
    const binData = binaryBuffer instanceof Uint8Array ? binaryBuffer : new Uint8Array(binaryBuffer);
    const fileSize = binData.length;
    this.otaTotalBytes = fileSize;
    const crc32 = calculateCRC32(binData);

    this.log(`[BLE FAST OTA] Bắt đầu phiên nâng cấp: ${fileSize} bytes, CRC-32: 0x${crc32.toString(16).toUpperCase()}...`, 'info');

    // Tạm dừng Heartbeat để dành 100% băng thông và hàng đợi truyền thông cho OTA
    this.stopHeartbeat();

    try {
      // 1. Gửi CMD_OTA_BEGIN (0x30) và chờ CMD_OTA_READY (0x31)
      const readyPromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          delete this.otaPendingResolvers.ready;
          reject(new Error('Quá thời gian chờ robot phản hồi OTA_READY (Timeout 5s)'));
        }, 5000);
        this.otaPendingResolvers.ready = { resolve, reject, timer };
      });

      const beginFrame = buildOtaBeginFrame(fileSize, crc32);
      await this.sendRawPacket(beginFrame);

      const readyInfo = await readyPromise;
      if (!readyInfo.isReady) {
        throw new Error(`Robot từ chối nâng cấp firmware (Mã trạng thái: 0x${readyInfo.statusCode.toString(16)})`);
      }

      const chunkSize = readyInfo.chunkSize || 480;
      const totalChunks = Math.ceil(fileSize / chunkSize);
      this.log(`Robot sẵn sàng nhận stream OTA: Khối ${chunkSize}B, Tổng ${totalChunks} chunks`, 'info');

      // 2. Stream các gói CMD_OTA_DATA (0x32)
      const startTime = Date.now();
      let bytesSent = 0;

      for (let i = 0; i < totalChunks; i++) {
        if (!this.isOtaActive || !this.isConnected) {
          throw new Error('Tiến trình nạp Firmware OTA đã bị hủy!');
        }

        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, fileSize);
        const chunkData = binData.slice(start, end);

        const dataPacket = buildOtaDataFrame(i, chunkData);

        // Soft Flow Control: Nếu gửi vượt quá gói robot đã ACK xác nhận (> 48 chunks ~ 23KB)
        // tạm hãm nhịp nhỏ (20ms) để ESP32 kịp hoàn tất ghi sector Flash SPI
        if (!this.isMockMode && this.otaLastAckChunk > 0 && (i - this.otaLastAckChunk > 48)) {
          await new Promise(r => setTimeout(r, 20));
        }

        // Truyền dạng Write Without Response để tối đa throughput
        await this.sendRawPacket(dataPacket);
        bytesSent += chunkData.length;
        this.otaBytesSent = bytesSent;

        // Tính toán thông số vận tốc và thời gian ước lượng
        const percent = Math.min(100, Math.round((bytesSent / fileSize) * 100));
        const elapsedSec = Math.max(0.01, (Date.now() - startTime) / 1000);
        const speedBytesPerSec = bytesSent / elapsedSec;
        const speedKbps = (speedBytesPerSec / 1024).toFixed(1);
        const remainingBytes = fileSize - bytesSent;
        const etaSeconds = speedBytesPerSec > 0 ? Math.max(0, Math.round(remainingBytes / speedBytesPerSec)) : 0;

        const progressInfo = {
          percent,
          speedKbps,
          etaSeconds,
          bytesSent,
          totalBytes: fileSize,
          chunkIdx: i + 1,
          totalChunks,
          confirmedChunk: this.otaLastAckChunk
        };

        if (progressCallback && this.isOtaActive) {
          progressCallback(progressInfo);
        }
        if (this.onOtaProgress && this.isOtaActive) {
          this.onOtaProgress(progressInfo);
        }

        // Pacing / Rate Limiting tối ưu theo Connection Interval BLE (~15-20ms)
        // Điều tiết tốc độ thực tế ~25-28 KB/s, trùng khớp với tốc độ ghi Flash thực tế của ESP32,
        // triệt tiêu hoàn toàn hiện tượng tràn đệm OS (Buffer Bloat) và giúp thanh Progress 100% khớp thực tế.
        if (this.isMockMode) {
          await new Promise(r => setTimeout(r, 4));
        } else {
          await new Promise(r => setTimeout(r, 16));
        }
      }

      if (!this.isOtaActive || !this.isConnected) {
        throw new Error('Tiến trình nạp Firmware OTA đã bị hủy!');
      }

      // 1. Chờ xả đệm Flash và radio an toàn (600ms) sau chunk cuối cùng
      this.log('Đã gửi xong 100% dữ liệu nhị phân. Chờ xả đệm vô tuyến và Flash (600ms)...', 'info');
      await new Promise(r => setTimeout(r, 600));

      // 2. Tính toán Dynamic Adaptive Timeout: tối thiểu 45s hoặc tự động co giãn theo dung lượng file
      const otaResultTimeoutMs = Math.max(45000, Math.ceil((fileSize / 20000) * 1000) + 15000);
      const timeoutSec = Math.round(otaResultTimeoutMs / 1000);
      this.log(`Đang kiểm tra mã băm toàn vẹn và nạp phân vùng Boot (Timeout an toàn: ${timeoutSec}s)...`, 'info');

      const resultPromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          delete this.otaPendingResolvers.result;
          reject(new Error(`Quá thời gian chờ phản hồi OTA_RESULT (Timeout ${timeoutSec}s)`));
        }, otaResultTimeoutMs);
        this.otaPendingResolvers.result = { resolve, reject, timer };
      });

      const endFrame = buildOtaEndFrame(crc32);
      this.otaEndFrameSent = true;
      // Gửi với độ ưu tiên cao nhất (isPriority = true) để không bị tồn sau các gói khác
      await this.sendRawPacket(endFrame, true);

      const resultInfo = await resultPromise;
      if (resultInfo.isSuccess) {
        this.log('[BLE FAST OTA] Nâng cấp Firmware THÀNH CÔNG! Robot đang khởi động lại...', 'success');
        if (this.onOtaResult) this.onOtaResult(resultInfo);
        return resultInfo;
      } else {
        throw new Error(`Nâng cấp Firmware thất bại: ${resultInfo.message} (Code: 0x${resultInfo.resultCode.toString(16)})`);
      }
    } finally {
      this.isOtaActive = false;
      this.otaEndFrameSent = false;
      this.otaBytesSent = 0;
      this.otaTotalBytes = 0;
      this.otaLastAckChunk = 0;
      this.startHeartbeat(); // Khôi phục lại nhịp Heartbeat sau phiên OTA
      if (this.otaPendingResolvers.ready) {
        clearTimeout(this.otaPendingResolvers.ready.timer);
        delete this.otaPendingResolvers.ready;
      }
      if (this.otaPendingResolvers.result) {
        clearTimeout(this.otaPendingResolvers.result.timer);
        delete this.otaPendingResolvers.result;
      }
    }
  }

  /**
   * Khởi động quy trình Fast OTA với kiểm tra CRC-32 trước khi stream
   */
  async startFastOta(binaryBuffer, expectedCrc32 = null, progressCallback = null) {
    if (typeof expectedCrc32 === 'function' && progressCallback === null) {
      progressCallback = expectedCrc32;
      expectedCrc32 = null;
    }
    const binData = binaryBuffer instanceof Uint8Array ? binaryBuffer : new Uint8Array(binaryBuffer);
    if (expectedCrc32) {
      const calculatedCrc = calculateCRC32(binData);
      const calcHex = calculatedCrc.toString(16).toUpperCase().padStart(8, '0');
      const expHex = String(expectedCrc32).replace(/^0x/i, '').toUpperCase().padStart(8, '0');
      if (calcHex !== expHex) {
        throw new Error(`Mã kiểm tra toàn vẹn CRC-32 không khớp! (Thực tế: 0x${calcHex}, Kỳ vọng: 0x${expHex})`);
      }
    }
    return this.uploadFirmwareOta(binData, progressCallback);
  }

  abortOta(reasonCode = 0x02) {
    if (this.isOtaActive) {
      this.isOtaActive = false;
      this.log('[BLE FAST OTA] Hủy phiên nâng cấp Firmware.', 'warning');
      const abortFrame = buildOtaAbortFrame(reasonCode);
      this.sendRawPacket(abortFrame).catch(() => {});

      if (this.otaPendingResolvers.ready) {
        clearTimeout(this.otaPendingResolvers.ready.timer);
        this.otaPendingResolvers.ready.reject(new Error('Phiên OTA bị hủy bởi người dùng'));
        delete this.otaPendingResolvers.ready;
      }
      if (this.otaPendingResolvers.result) {
        clearTimeout(this.otaPendingResolvers.result.timer);
        this.otaPendingResolvers.result.reject(new Error('Phiên OTA bị hủy bởi người dùng'));
        delete this.otaPendingResolvers.result;
      }
    }
  }

  /**
   * Yêu cầu robot gửi dữ liệu nhật ký hoạt động (5 phút gần nhất trên RAM)
   * @param {number} timeoutMs - Thời gian timeout chờ (mặc định 6000ms)
   * @returns {Promise<string>} - Chuỗi log hoàn chỉnh
   */
  async requestLogs(timeoutMs = 6000) {
    if (!this.isConnected) {
      throw new Error('Chưa kết nối với Robot! Vui lòng kết nối BLE trước.');
    }

    return new Promise(async (resolve, reject) => {
      let accumulatedLogs = '';
      const timer = setTimeout(() => {
        delete this.logStreamResolver;
        if (accumulatedLogs.length > 0) {
          resolve(accumulatedLogs);
        } else {
          reject(new Error('Quá thời gian chờ phản hồi log từ Robot (Timeout 6s)'));
        }
      }, timeoutMs);

      this.logStreamResolver = {
        onChunk: (chunkText, isLast) => {
          accumulatedLogs += chunkText;
          if (isLast) {
            clearTimeout(timer);
            delete this.logStreamResolver;
            resolve(accumulatedLogs);
          }
        },
        onError: (err) => {
          clearTimeout(timer);
          delete this.logStreamResolver;
          reject(err);
        }
      };

      try {
        const getLogsPacket = buildGetLogsPacket();
        await this.sendRawPacket(getLogsPacket);
      } catch (err) {
        clearTimeout(timer);
        delete this.logStreamResolver;
        reject(err);
      }
    });
  }

  handleIncomingBytes(bytes) {
    if (!bytes || bytes.length === 0) return;

    // 1. Kiểm tra gói phản hồi kết quả OTA dạng nhị phân thô 2 bytes: [0x34, status] (CMD_OTA_RESULT)
    if (bytes[0] === COMMANDS.OTA_RESULT && bytes.length >= 2) {
      const resultCode = bytes[1];
      const isSuccess = (resultCode === 0x00);
      const resultInfo = {
        resultCode,
        isSuccess,
        rebootCountdown: 2,
        message: isSuccess ? 'Nâng cấp firmware thành công! Đang khởi động lại...' : 'Lỗi nạp firmware'
      };

      if (this.otaPendingResolvers.result) {
        this.log(`[BLE FAST OTA] Nhận phản hồi OTA_RESULT (Raw Opcode 0x34): Code 0x${resultCode.toString(16)} -> ${isSuccess ? 'Thành công' : 'Thất bại'}!`, isSuccess ? 'success' : 'error');
        clearTimeout(this.otaPendingResolvers.result.timer);
        this.otaPendingResolvers.result.resolve(resultInfo);
        delete this.otaPendingResolvers.result;
      }
      return;
    }

    const frame = parseFrame(bytes);
    if (!frame) return;

    switch (frame.cmdId) {
      case COMMANDS.TELEMETRY: {
        const telemetry = parseTelemetry(frame.payload);
        if (telemetry) {
          if (telemetry.firmwareVersion) {
            this.firmwareVersion = telemetry.firmwareVersion;
          }
          if (this.onTelemetry) {
            this.onTelemetry(telemetry);
          }
        }
        break;
      }

      case COMMANDS.RUNTIME_ERROR: {
        this.abortUpload();
        const errorInfo = parseRuntimeError(frame.payload);
        if (errorInfo) {
          const displayLine = (this.scriptLineOffset && errorInfo.lineNum > 6)
            ? Math.max(1, errorInfo.lineNum - this.scriptLineOffset)
            : errorInfo.lineNum;
          const adjustedErrorInfo = { ...errorInfo, lineNum: displayLine };
          this.log(`[LỖI DÒNG ${displayLine} - ${errorInfo.errorType}]: ${errorInfo.message}`, 'error');
          if (this.onRuntimeError) {
            this.onRuntimeError(adjustedErrorInfo);
          }
        }
        break;
      }

      case COMMANDS.OTA_READY: {
        const readyInfo = parseOtaReady(frame.payload);
        if (this.otaPendingResolvers.ready) {
          clearTimeout(this.otaPendingResolvers.ready.timer);
          this.otaPendingResolvers.ready.resolve(readyInfo);
          delete this.otaPendingResolvers.ready;
        }
        break;
      }

      case COMMANDS.OTA_RESULT: {
        const resultInfo = parseOtaResult(frame.payload);
        if (this.otaPendingResolvers.result) {
          this.log(`[BLE FAST OTA] Nhận phản hồi OTA_RESULT (Khung chuẩn 0x34): Code 0x${resultInfo?.resultCode?.toString(16)}`, 'success');
          clearTimeout(this.otaPendingResolvers.result.timer);
          this.otaPendingResolvers.result.resolve(resultInfo);
          delete this.otaPendingResolvers.result;
        }
        break;
      }

      case COMMANDS.OTA_ABORT: {
        const abortInfo = parseOtaAbort(frame.payload);
        this.log(`[BLE FAST OTA] Nhận tín hiệu hủy từ robot: ${abortInfo.message}`, 'warning');
        this.abortOta();
        break;
      }

      case COMMANDS.OTA_ACK: {
        const ackInfo = parseOtaAck(frame.payload);
        if (ackInfo) {
          this.otaLastAckChunk = ackInfo.chunkIdx;
          if (this.onOtaAck) {
            this.onOtaAck(ackInfo);
          }
        }
        break;
      }

      case COMMANDS.LOG_DATA: {
        const logData = parseLogDataPacket(frame.payload);
        if (logData) {
          if (this.logStreamResolver) {
            this.logStreamResolver.onChunk(logData.textChunk, logData.isLast);
          }
          if (this.onLogChunk) {
            this.onLogChunk(logData);
          }
        }
        break;
      }

      default:
        // Other incoming commands
        break;
    }
  }
}
